import { CANONICAL_ORIGIN, whatsappUrl } from "@/config/site.config";
import { EDUANANT } from "./eduanant.js";
import { OFFICE, BASE_SERVICES, REGISTRATIONS, LIABILITY_STATEMENT, registration, BANKING_NAME, PAYMENT_NOTICE } from "./company.js";

export { OFFICE, REGISTRATIONS, LIABILITY_STATEMENT, registration, BANKING_NAME, PAYMENT_NOTICE };

/** @deprecated Import CANONICAL_ORIGIN from "@/config/site.config" in new code. */
export const SITE_URL = CANONICAL_ORIGIN;

/**
 * Company slogan — defined once so it can be changed everywhere in a single edit.
 * (It appears in the hero, About, the footer and the intro animation.)
 */
export const SLOGAN = "Think Innovative.";

export const NAV_LINKS = [
  { to: "/products", label: "Products", testId: "nav-products-link" },
  { to: "/services", label: "Services", testId: "nav-services-link" },
  { to: "/work", label: "Work", testId: "nav-work-link" },
  { to: "/blog", label: "Insights", testId: "nav-blog-link" },
  { to: "/about", label: "About", testId: "nav-about-link" },
];

export const EDUANANT_URL = EDUANANT.url;

export const SOCIALS = [
  { name: "Facebook", url: "https://www.facebook.com/snapxtechnologies/" },
  { name: "Instagram", url: "https://www.instagram.com/snapxtechnologies/" },
  { name: "LinkedIn", url: "https://www.linkedin.com/company/snapxtechnologies/" },
  { name: "WhatsApp", url: whatsappUrl() },
];

export const TIERS = [
  { key: "products", label: "Products", note: "Our own software, live in production." },
  { key: "technology", label: "Technology Services", note: "Design and engineering for clients." },
  { key: "growth", label: "Growth Services", note: "Brand, motion, marketing and finance." },
];

export const MANIFESTO = [
  {
    number: "01",
    title: "We build products.",
    body: `EduAnant, our school operating system, has run ${EDUANANT.liveSchool.name} every working day since ${EDUANANT.liveSchool.since}. It is not a pitch deck — it is a live deployment, a public release history and a roadmap. Everything we promise you, we have already done for ourselves.`,
  },
  {
    number: "02",
    title: "We build brands.",
    body: "Identities, films and design systems bold enough to be remembered in the noisiest market on earth. Strategy first, decoration never.",
  },
  {
    number: "03",
    title: "We build businesses.",
    body: "Web, mobile, software, marketing and even GST compliance — one senior team owning the whole journey from first sketch to first crore.",
  },
];

/** The four-step delivery process shown on every service page. */
export const PROCESS = [
  { title: "Discover", body: "We interrogate the problem before we touch a pixel." },
  { title: "Design", body: "Strategy made visible — reviewed with you, weekly." },
  { title: "Build", body: "Senior hands only. Shipped in slices you can use." },
  { title: "Compound", body: "Launch is the start. We measure, learn, improve." },
];

/**
 * How engagements are structured. Deliberately no rupee figures — every scope differs,
 * and a number without a scope behind it misleads more than it helps.
 */
export const ENGAGEMENT_MODELS = [
  {
    key: "fixed-scope",
    name: "Fixed scope",
    tagline: "A defined outcome, a defined price.",
    body: "You know what you need built. We scope it precisely, quote once, and deliver against that scope. No hourly meter, no surprise invoice at the end.",
    bestFor: ["Websites and marketing sites", "Brand identity projects", "Explainer films", "Well-defined MVPs"],
    commitment: "One-off project",
    includes: ["Written scope before any work starts", "Fixed price for that scope", "Weekly review builds", "Handover and documentation"],
  },
  {
    key: "retainer",
    name: "Monthly retainer",
    tagline: "A senior team on tap, every month.",
    body: "For work that never really finishes — product iteration, performance marketing, DevOps, compliance. You get a reserved allocation of the same people who know your system.",
    bestFor: ["Live products needing iteration", "Performance marketing", "Cloud & DevOps", "Finance, tax and GST"],
    commitment: "Rolling monthly, 30 days' notice",
    includes: ["Reserved capacity each month", "Named team you keep", "Weekly performance call", "Priority response"],
  },
  {
    key: "dedicated-team",
    name: "Dedicated team",
    tagline: "Your engineering department, staffed by us.",
    body: "A full cross-functional squad working only on your product — design, engineering, QA — reporting into your roadmap as if they were employees, without the hiring cycle.",
    bestFor: ["Funded startups scaling fast", "Multi-quarter platform builds", "Teams replacing a failed vendor"],
    commitment: "Three months minimum",
    includes: ["Cross-functional squad", "Your tools, your roadmap", "Daily standups with your team", "Scale the squad up or down quarterly"],
  },
];

/** What every engagement includes, regardless of model. */
export const ENGAGEMENT_GUARANTEES = [
  { title: "You own everything", body: "Code in your repository, infrastructure in your cloud account, design files in your workspace. No licence fees to us, ever." },
  { title: "Senior hands only", body: "The people in the pitch are the people on the work. We do not staff juniors against a senior rate card." },
  { title: "Weekly, visible progress", body: "A staging build or review file you can click through every week — not a status update saying it is going well." },
  { title: "A written scope first", body: "Nothing starts until you have a document saying exactly what will be delivered. Changes go through a written change note." },
];

/**
 * Team structure. Names and photos are supplied per person — add `name` and set
 * `photo` to a file under `public/assets/team/` and the card fills in automatically.
 */
export const TEAM = [
  { key: "founder", role: "Founder & Product", name: "", photo: null, bio: "Sets the product direction for EduAnant and owns every client relationship end to end." },
  { key: "engineering", role: "Engineering Lead", name: "", photo: null, bio: "Owns architecture, code quality and the fact that things stay up after launch." },
  { key: "design", role: "Design Lead", name: "", photo: null, bio: "Runs research, product design and the brand systems that keep everything coherent." },
  { key: "growth", role: "Growth Lead", name: "", photo: null, bio: "Performance marketing, SEO and the analytics that prove which of it worked." },
  { key: "finance", role: "Finance & Compliance", name: "", photo: null, bio: "GST, bookkeeping and incorporation — with CA oversight on everything filed." },
];

import caseStudiesData from "./caseStudies.json";
import testimonialsData from "./testimonials.json";
import { SERVICE_DETAILS } from "./serviceDetails.js";

export const FALLBACK_TESTIMONIALS = testimonialsData;
export const FALLBACK_CASE_STUDIES = caseStudiesData;

/**
 * Services, decorated with their detail payload and with related case studies derived
 * from each study's `serviceSlugs`. Deriving rather than hardcoding means adding a
 * case study automatically surfaces it on the right service pages.
 */
export const SERVICES = BASE_SERVICES.map((service) => ({
  ...service,
  ...(SERVICE_DETAILS[service.slug] || {}),
  relatedWork: caseStudiesData
    .filter((study) => (study.serviceSlugs || []).includes(service.slug))
    .map((study) => study.slug),
}));

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
export const getCaseStudy = (slug) => caseStudiesData.find((s) => s.slug === slug);
export const getRelatedStudies = (service) =>
  (service?.relatedWork || []).map(getCaseStudy).filter(Boolean);

